// sentItemsClient — the web mirror of the `SentItem` contract.
//
// Server side: apps/api/src/lib/sentItems/types.ts. That file is the source of
// intent; this one is the SAME contract in the shapes the SPA already speaks.
// The two files' exported types are asserted equal — MEMBER BY MEMBER, each
// with its optionality and its type text — by
// apps/api/src/lib/sentItems/contractParity.test.ts. The WEL-144 build lost
// days to server/client drift (spec C2b), so a type added, renamed or reshaped
// on one side without the other fails the API suite rather than a page at
// runtime. Where a shape is ALIASED to a name the SPA already had, the test
// follows the alias and compares the members underneath it.
//
// Two things to know before editing:
//
//   • The web cannot import across the workspace boundary, so every shared
//     shape is taken from the client modules that ALREADY declare it —
//     viewDocsClient (`ViewDocPanel`, `DocRecipient`, `DocFile`,
//     `DocWithheldNote`…) and commandCenterClient (`SignalCounts`, `Signal`,
//     `SubmissionRow`, `RailStep`, `CasePerson`…). Nothing here is a second
//     copy of a shape the SPA already binds to; where a name differs between
//     the two sides it is ALIASED, once, with a comment saying so.
//
//   • W0 ships TYPES ONLY. The fetchers (`fetchSentItems`, `fetchSentItem`,
//     `fetchHouseholds`…) and `itemIdForThreadId` land in W4/W5/W6, in this
//     same file. They are wiring, not shape: not parity-checked; only types are.
//
// Contract shorthand (plan §4): a SentItem is one document/request for its
// whole life — one row from draft to signed to synced — carrying the thread
// model's standing on top of the WEL-144 document model.

import { api } from '@/lib/api';
import type {
  ActivityEvent,
  CaseAction,
  CasePerson,
  DurableCaseMember,
  DurableCasePayload,
  RailStep,
  Signal,
  SignalCounts,
  SubmissionRow,
} from '@/lib/commandCenterClient';
import type {
  DocActivityEvent,
  DocFacetOption,
  DocFile,
  DocRecipient,
  DocWithheldNote,
  ViewDocPanel,
  ViewDocReplica,
} from '@/lib/viewDocsClient';
import type { AppfolioSyncDisplay } from '@/lib/appfolioSync';

/**
 * Where a document (or one of its versions) stands — the WEL-144 vocabulary.
 *
 * Declared literally HERE rather than imported, because viewDocsClient types
 * its states as plain `string` (its rows are deliberately loose so a fixture
 * can be three fields wide). The server's copy is the union in
 * apps/api/src/lib/viewDocs/docTypes.ts; keep the two in step.
 */
export type ViewDocState =
  | 'draft'
  | 'sent'
  | 'in_progress'
  | 'completed'
  | 'declined'
  | 'cancelled'
  | 'voided'
  | 'superseded';

// ---------------------------------------------------------------------------
// Kinds (plan §5.1)
// ---------------------------------------------------------------------------

/**
 * One row kind: every `ViewDocKind` except the three `intake_*` document kinds,
 * plus `'intake'` — a household's three intake sections are ONE row with three
 * sections (Q2), and `'intake'` is a ROW kind the viewDocs registry does not
 * know (T23: never pass it to a registry lookup; resolve per section instead).
 *
 * A renewal is a `lease` row with `isRenewal: true`, not a kind of its own.
 */
export type SentItemKind =
  | 'lease'
  | 'adoption_addendum'
  | 'leaseholder_change'
  | 'leaseholder_removal'
  | 'payment_plan'
  | 'animal_addendum'
  /** WEL-153 — the PAID Animal Addendum (a current tenant adding an animal). A
   *  separate kind from `animal_addendum`, the fee-free accommodation one. */
  | 'animal_addendum_paid'
  | 'condition_report'
  | 'moveout_funds_direction'
  | 'manual_document'
  | 'property_management_agreement'
  | 'owner_contact_addendum'
  | 'w9'
  | 'city_utility_agreement'
  | 'intake'
  | 'rental_verification'
  | 'owner_intake'
  | 'ccr_upload'
  | 'rra_intake'
  | 'rra_verification';

/** Every row kind, in the order the type chips render. */
export const SENT_ITEM_KINDS: readonly SentItemKind[] = [
  'lease',
  'adoption_addendum',
  'leaseholder_change',
  'leaseholder_removal',
  'payment_plan',
  'animal_addendum',
  'animal_addendum_paid',
  'condition_report',
  'moveout_funds_direction',
  'manual_document',
  'property_management_agreement',
  'owner_contact_addendum',
  'w9',
  'city_utility_agreement',
  'intake',
  'rental_verification',
  'owner_intake',
  'ccr_upload',
  'rra_intake',
  'rra_verification',
];

/** The filter-chip bucket a row belongs to (§6.2). No `'renewal'` — the
 *  Renewal chip selects `lease` rows carrying `isRenewal`. */
export type SentItemTypeGroup =
  | 'lease'
  | 'intake'
  | 'verification'
  | 'condition_report'
  | 'addendum'
  | 'payment'
  | 'moveout'
  | 'owner'
  | 'accommodation'
  | 'other';

/** Every type group, in chip order. */
export const SENT_ITEM_TYPE_GROUPS: readonly SentItemTypeGroup[] = [
  'lease',
  'intake',
  'verification',
  'condition_report',
  'addendum',
  'payment',
  'moveout',
  'owner',
  'accommodation',
  'other',
];

/** Chip labels — the server's facet labels, same table. */
export const SENT_ITEM_TYPE_GROUP_LABEL: Record<SentItemTypeGroup, string> = {
  lease: 'Lease',
  intake: 'Intake',
  verification: 'Verification',
  condition_report: 'Condition report',
  addendum: 'Addenda',
  payment: 'Payment plan',
  moveout: 'Move-out',
  owner: 'Owner',
  accommodation: 'Accommodation',
  other: 'Other',
};

/** Kind → chip bucket. Must match the server's copy or a chip's count stops
 *  matching what clicking it returns. */
export const TYPE_GROUP_BY_KIND: Record<SentItemKind, SentItemTypeGroup> = {
  lease: 'lease',
  adoption_addendum: 'addendum',
  leaseholder_change: 'addendum',
  leaseholder_removal: 'addendum',
  animal_addendum: 'addendum',
  // WEL-153 folds into the EXISTING 'addendum' bucket, so SENT_ITEM_TYPE_GROUPS
  // stays at 10 and no new filter chip appears. Deliberate, not an oversight: a
  // new kind usually does earn a chip, and the two animal addenda are the same
  // thing to somebody filtering (an addendum about an animal) even though they
  // are different documents to the system. Splitting them would put two nearly
  // identical chips side by side and make the operator guess which one holds
  // the row they want.
  animal_addendum_paid: 'addendum',
  payment_plan: 'payment',
  moveout_funds_direction: 'moveout',
  manual_document: 'other',
  condition_report: 'condition_report',
  intake: 'intake',
  rental_verification: 'verification',
  property_management_agreement: 'owner',
  owner_contact_addendum: 'owner',
  w9: 'owner',
  city_utility_agreement: 'owner',
  owner_intake: 'owner',
  ccr_upload: 'owner',
  rra_intake: 'accommodation',
  rra_verification: 'accommodation',
};

// ---------------------------------------------------------------------------
// Lifecycle (plan §5.3)
// ---------------------------------------------------------------------------

/** Where the row's ONE item sits. Polling, sending and syncing are STEPS of
 *  the item's single row, never rows of their own (Q3, T13). */
export type LifecycleStep =
  | 'drafting'
  | 'generated'
  | 'sending'
  | 'out'
  | 'countersign'
  | 'executed'
  | 'syncing'
  | 'synced'
  | 'closed';

/** Every step, in lifecycle order. */
export const LIFECYCLE_STEPS: readonly LifecycleStep[] = [
  'drafting',
  'generated',
  'sending',
  'out',
  'countersign',
  'executed',
  'syncing',
  'synced',
  'closed',
];

/**
 * The Overview's phase vocabulary (mirrors apps/api/src/lib/sentItems/types.ts).
 * Coarser than `LifecycleStep`: `out` folds `sending`, `done` folds executed +
 * syncing + synced, and `blocked` is a position of its own.
 */
export type OverviewPhase =
  | 'preparing'
  | 'ready'
  | 'out'
  | 'countersign'
  | 'done'
  | 'blocked'
  | 'closed';

export const OVERVIEW_PHASES: readonly OverviewPhase[] = [
  'preparing',
  'ready',
  'out',
  'countersign',
  'done',
  'blocked',
  'closed',
];

/** What a lane selects, in the list's own filter vocabulary. */
export interface SentItemsLaneSelect {
  types: SentItemTypeGroup;
  renewals?: 'only' | 'exclude';
}

export interface SentItemsLane {
  id: string;
  label: string;
  select: SentItemsLaneSelect;
  phases: Partial<Record<OverviewPhase, number>>;
  total: number;
}

/** GET /api/sent-items/lanes */
export interface SentItemsLanesResponse {
  lanes: SentItemsLane[];
  generatedAt: string;
}

/** Steps at which an item has stopped moving — the `inMotion` complement. */
export const FINISHED_LIFECYCLE_STEPS: readonly LifecycleStep[] = [
  'executed',
  'synced',
  'closed',
];

/** The step, said out loud, plus why it is stuck when it is. */
export interface ItemLifecycle {
  step: LifecycleStep;
  /** Operator-facing, kind-aware ("Out for signature", "Waiting on WHP"). */
  label: string;
  /** The specific thing happening: "Pulling from zInspector", a sync failure. */
  detail?: string;
  /** Something must be fixed before this moves. Feeds the Blocked filter. */
  blocked?: boolean;
}

// ---------------------------------------------------------------------------
// Waiting on (plan §5.4)
// ---------------------------------------------------------------------------

/** Whose move it is. Ranks the `waiting` view: whp 0, recipient 1, system 2. */
export type WaitingParty = 'recipient' | 'whp' | 'system';

export interface WaitingOn {
  party: WaitingParty;
  /** People, in the order they were invited. Empty for 'system' and drafts. */
  names: string[];
  /** The row's one line: "Dana Fairweather · 2 days". */
  label: string;
}

// ---------------------------------------------------------------------------
// Versions (plan §5.1, T2/T3)
// ---------------------------------------------------------------------------

/** One version inside an item's row. `docKind`/`docId` address it through the
 *  EXISTING `GET /api/docs/:kind/:id` loader — no second projection. */
export interface ItemVersion {
  id: string;
  docKind: string;
  docId: string;
  label: string;
  state: ViewDocState;
  stateLabel: string;
  createdAt: string;
  sentAt?: string;
  /** The version the viewer opens by default. Exactly one is true. */
  current: boolean;
  /** An OLDER record that is still live — the "Revise & keep open" shape. The
   *  row's state follows the newest record, `waitingOn` the newest OPEN one. */
  stillOpen?: boolean;
  /** Whether `/file` can serve bytes (never for a W-9 — T6). */
  viewable: boolean;
}

// ---------------------------------------------------------------------------
// Intake sections (plan §5.5)
// ---------------------------------------------------------------------------

/** The three tenant-intake sections. `rra_intake` is NOT one of them (T12). */
export type IntakeSectionKey = 'designation' | 'yard_care' | 'animals';

/** A section's state. `not_requested` is SHOWN, muted — never hidden (§14 Q1). */
export type IntakeSectionStatus = 'not_requested' | 'requested' | 'completed' | 'cancelled';

/** One section of the single intake row. */
export interface IntakeSectionSummary {
  section: IntakeSectionKey;
  label: string;
  status: IntakeSectionStatus;
  requestedAt?: string;
  completedAt?: string;
  /** 'intake_designation' | 'intake_yard_care' | 'intake_animals'. */
  docKind: string;
  docId: string;
}

/** A section on the DETAIL response: the summary plus its rendered form. */
export interface IntakeSectionDetail extends IntakeSectionSummary {
  panel: ViewDocPanel;
  replica?: ViewDocReplica;
}

// ---------------------------------------------------------------------------
// The row (plan §5.2)
// ---------------------------------------------------------------------------

export interface SentItemRow {
  /** `<kind>:<nativeId>` — the `/items/:itemId` URL id. */
  itemId: string;
  kind: SentItemKind;
  /** Registry label; "Renewal" when `isRenewal`; "Intake" for the intake row. */
  kindLabel: string;
  typeGroup: SentItemTypeGroup;
  /** A `renewal-<ulid>` lease (T16). Drives the Renewal chip and label only. */
  isRenewal?: boolean;
  /** The people, comma-joined. */
  subject: string;
  propertyLabel: string | null;
  unitLabel: string | null;
  /** `null` for a W-9, on purpose (T25). */
  caseId: string | null;
  caseKind: 'household' | 'owner' | null;
  groupId: string | null;
  state: ViewDocState;
  /** Bespoke where the kind has better words ("1 of 3 signed"). */
  stateLabel: string;
  lifecycle: ItemLifecycle;
  /** Null exactly when the item is finished. */
  waitingOn: WaitingOn | null;
  /** ISO instant the current wait began — the earliest unanswered `sentAt`,
   *  not the last reminder. Null when finished. */
  since: string | null;
  inMotion: boolean;
  /** `max(latest event.at, record.updatedAt)` — drafts write no events (T19). */
  lastActivityAt: string;
  /** First send. Null for a row that has never gone out (T14). */
  sentAt: string | null;
  completedAt: string | null;
  signals: SignalCounts;
  /** The registry panel digest of the CURRENT version. Never empty (T11). */
  digest: string;
  versionCount: number;
  /** False for a W-9 and anything the registry marks unviewable (T6). */
  viewable: boolean;
  resendable: boolean;
  /** Present only for `intake` and `owner_intake` (§5.5). */
  sections?: IntakeSectionSummary[];
  /** The withheld row stub: type only, not clickable. The list route OMITS
   *  restricted rows, so this should never arrive — the renderer is the net. */
  redacted?: boolean;
}

// ---------------------------------------------------------------------------
// Where it stands (plan §8.4)
// ---------------------------------------------------------------------------

/** A household's standing, derived purely from its own items (Q7). Computed
 *  over the rows THIS VIEWER may see (T5). */
export interface WhereItStands {
  /** One line: "Lease out · waiting on Dana Fairweather · 2 days". */
  line: string;
  itemId: string | null;
  tone: 'active' | 'blocked' | 'done' | 'empty';
}

// ---------------------------------------------------------------------------
// Actions (plan §6.6)
// ---------------------------------------------------------------------------

/** `'navigate'` is a link/builder action: `endpoint` is an href to route to. */
export type ItemActionMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'navigate';

/** Button emphasis. Do NOT map 'primary' onto `data-variant="primary"` — that
 *  attribute has no CSS rule at all (T29); primary is the default button. */
export type ItemActionTone = 'default' | 'primary' | 'danger';

/** One action, carrying the EXISTING endpoint/method/body it needs (§6.6). */
export interface ItemAction {
  id: string;
  label: string;
  method: ItemActionMethod;
  endpoint: string;
  body?: Record<string, unknown>;
  tone?: ItemActionTone;
  confirm?: string;
}

/** One step of the signing rail. Alias of the thread page's existing shape. */
export type ItemRailStep = RailStep;

// ---------------------------------------------------------------------------
// List + detail wire shapes (plan §7.1, §7.2)
// ---------------------------------------------------------------------------

/** One filter chip with its count — the existing facet shape. */
export type SentItemFacet = DocFacetOption;

/** The three switchable views (Q4). Default `all`. */
export type SentItemsView = 'all' | 'waiting' | 'recent';

/** Every view, in segmented-control order. */
export const SENT_ITEMS_VIEWS: readonly SentItemsView[] = ['all', 'waiting', 'recent'];

/** GET /api/sent-items query params (§7.1). All of them live in the URL so a
 *  view is shareable; the SERVER sorts, the client never re-sorts a page. */
export interface SentItemsListParams {
  view?: SentItemsView;
  types?: SentItemTypeGroup[];
  renewalsOnly?: boolean;
  state?: ViewDocState;
  /** EXACT lifecycle step (sol finding 11) — what Overview's phase segments
   *  drill into. `state` and `party` cannot express "generated, never sent". */
  step?: LifecycleStep;
  /** An Overview PHASE (the workflow lanes' grouping) — see `OverviewPhase`. */
  phase?: OverviewPhase;
  /** The Leases lane: `lease` rows WITHOUT `isRenewal`. */
  excludeRenewals?: boolean;
  party?: WaitingParty;
  blocked?: boolean;
  q?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
  /** `case:<caseId>` | `group:<groupId>` | `owner:<caseId>`. */
  scope?: string;
}

/** GET /api/sent-items. */
export interface SentItemsListResponse {
  items: SentItemRow[];
  total: number;
  nextCursor: string | null;
  facets: {
    typeGroups: SentItemFacet[];
    states: SentItemFacet[];
    parties: SentItemFacet[];
  };
  /** Kinds the registry does not cover — said out loud, not dropped (T23). */
  gaps: Array<{ kind: string; count: number }>;
  /** C9: emitted UNCONDITIONALLY for an off-roster viewer. Never a count. */
  withheld: DocWithheldNote[];
  generatedAt: string;
}

// ---------------------------------------------------------------------------
// Owner intake — the management surface for the EPMA's pre-signature phase
// ---------------------------------------------------------------------------

/** One owner on an intake-phase EPMA. `role` is the `Owner{N}` KEY the
 *  email-swap endpoint takes, not a label. */
export interface OwnerIntakeManagementOwner {
  role: string;
  /** The party name as the agreement reads it (an entity is its own name). */
  name: string;
  /** Empty string when there is no address on file — never null. */
  email: string;
  ownerType: 'individual' | 'entity';
  /** First-completion-wins: true only once the WHOLE intake locked. */
  completed: boolean;
}

/**
 * The `owner_intake` item's management surface (sol finding 6) — per-owner
 * status + email swap, reminder state, Restart reminders, Modify, Cancel.
 *
 * The builder seed (`staffPrefill`) and the owners' `answers` are deliberately
 * NOT here: Modify re-opens the builder, which loads them itself from
 * `GET /api/owner-intake/:groupId/detail`.
 */
export interface OwnerIntakeManagement {
  groupId: string;
  status: 'intake_sent' | 'in_progress' | 'generating' | 'ready_to_sign' | 'cancelled';
  /** Bumped by a staff "restart intake" — shown only when > 1. */
  round: number;
  propertyAddress: string;
  owners: OwnerIntakeManagementOwner[];
  reminders: { count: number; lastReminderAt?: string; anchorAt?: string };
  /** Has ANY owner submitted answers yet. */
  intakeGathered: boolean;
  createdAt: string;
  updatedAt: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelledByDisplay?: string;
  cancelledReason?: string;
}

/**
 * The Owner Contact form that follows an executed agreement, while it is out for
 * the owners AND after staff cancelled it (WEL-181/182). It has no item of its
 * own, so it rides the agreement's Full Details; a cancel stays visible and
 * undoable. Each action is the same `ItemAction` the panel runs.
 */
export interface OwnerContactFormPanel {
  status: 'intake_sent' | 'in_progress' | 'cancelled';
  /** The owners it went to, comma-joined as the agreement names them. */
  sentTo: string;
  /** ISO instant the form went out. */
  sentAt: string;
  /** `status: 'cancelled'` only — when staff cancelled it, and who (display name). */
  cancelledAt?: string;
  cancelledBy?: string;
  /** While the form is OUT — the one way to withdraw it. */
  cancel?: ItemAction;
  /** While it is CANCELLED — send it again (a fresh round; the old links work again). */
  sendAgain?: ItemAction;
}

/** GET /api/sent-items/:itemId (§7.2). */
export interface SentItemDetailResponse {
  row: SentItemRow;
  /** The panel of the SELECTED version — never live state (T10). */
  panel: ViewDocPanel;
  replica?: ViewDocReplica;
  file?: DocFile;
  /** Newest first; exactly one `current`. */
  versions: ItemVersion[];
  selectedVersionId: string;
  recipients: DocRecipient[];
  /** Newest LAST (T18) — reverse in the view, not in the API. */
  activity: DocActivityEvent[];
  pending: string[];
  fillOnBehalf: boolean;
  actions: ItemAction[];
  /** This item's signals IN FULL (sol finding 16) — `row.signals` is the count
   *  a list row shows; these are the ones you can act on. Absent for an item
   *  with no thread (a W-9, an accommodation row). */
  signals?: Signal[];
  /** `kind: 'owner_intake'` only — the pre-signature management surface. */
  ownerIntake?: OwnerIntakeManagement;
  /** `kind: 'property_management_agreement'` only, while the Owner Contact form is out. */
  ownerContactForm?: OwnerContactFormPanel;
  /** `kind: 'intake'` / `'owner_intake'` only. */
  sections?: IntakeSectionDetail[];
  rail?: ItemRailStep[];
  submissions?: SubmissionRow[];
  appfolioSync?: AppfolioSyncDisplay | null;
  /** `kind: 'condition_report'` only — the corrected-version surface. */
  conditionReportCorrection?: ConditionReportCorrectionPanel;
}

/**
 * Where a condition report's corrected version stands ("Request new signatures",
 * 2026-10-06), and whether staff may ask for one now. Names and dates only.
 */
export interface ConditionReportCorrectionPanel {
  state: 'none' | 'pending' | 'withdraw_unknown' | 'took_effect' | 'withdrawn';
  canRequest: boolean;
  /** A pending correction has residents whose invite SES has not accepted yet. */
  canResend: boolean;
  /** Offer "Finish sending the corrected version" (unsent invites, or created-but-unrecorded). */
  canFinish: boolean;
  requestEndpoint: string;
  withdrawEndpoint: string;
  /** POST `{ outcome: 'withdrawn' | 'pending' }` — resolves 'withdraw_unknown'. */
  resolveWithdrawalEndpoint: string;
  requestedAt: string | null;
  requestedBy: string | null;
  /** The resident-facing "what changed" note, when one was given. */
  whatChanged: string | null;
  tookEffectAt: string | null;
  withdrawnAt: string | null;
}

// ---------------------------------------------------------------------------
// Households (plan §7.4, §7.5)
// ---------------------------------------------------------------------------

/** What AppFolio says this household is to us — a column and a filter, not a
 *  stage (Q10). `none` = a case with no application behind it. */
export type HouseholdRelationship = 'applying' | 'approved' | 'tenant' | 'past' | 'none';

/** GET /api/households `filter=`. Default `motion`. */
export type HouseholdsFilter = 'motion' | 'all' | 'closed';

/** GET /api/households query params (§7.4) — server-side now, not client (T27). */
export interface HouseholdsListParams {
  /** `motion` = `inMotionCount > 0 OR relationship ∈ {applying, approved}` —
   *  an approved-not-yet-converted household is exactly the one whose lease
   *  WHP still has to build (WEL-147). */
  filter?: HouseholdsFilter;
  q?: string;
  /** Any of these match (wire: comma-separated). One entry behaves as before;
   *  the page's "Applicants" option sends ['applying','approved'] (WEL-147). */
  relationship?: HouseholdRelationship[];
  limit?: number;
  cursor?: string;
}

/** One row of the merged Applications + Households list (§8.2). */
/**
 * Where the household's TENANT INTAKE stands (sol finding 10) — the old
 * Applications "Forms n/total" badge, restored.
 *
 * ⚠️ POSITION ONLY: counts and section names, never an applicant's answers.
 */
export type HouseholdIntakeState = 'not_requested' | 'requested' | 'partial' | 'complete';

export interface HouseholdIntakeSummary {
  state: HouseholdIntakeState;
  /** Sections actually asked for — requested + completed, cancelled excluded. */
  requested: number;
  completed: number;
  /** A still-outstanding section has been out longer than the attention age. */
  stale: boolean;
  /** The chip's words: "Intake 1 of 3", "Intake complete". */
  label: string;
}

/** The household's prior-landlord verifications, as the old advisory
 *  "Verification concern" pill read them. Advisory — it gates nothing. */
export interface HouseholdVerificationSummary {
  total: number;
  /** Sent or reminded and still unanswered. */
  pending: number;
  /** The landlord answered. */
  responded: number;
  concern: boolean;
  /** Staff marked every not-clean record reviewed. */
  cleared: boolean;
  /** The chip's words; null when there is nothing worth a chip. */
  label: string | null;
}

/** Which lease CTA the row offers — the four states the Applications page
 *  distinguished, plus the two post-send ones. */
export type HouseholdLeaseState =
  | 'none'
  | 'new'
  | 'resume_draft'
  | 'unsaved_changes'
  | 'generated'
  | 'sent'
  | 'executed';

/** The row's lease CTA. */
export interface HouseholdLeaseSummary {
  state: HouseholdLeaseState;
  /** "New lease", "Resume draft", "View lease"… Empty only when `none`. */
  label: string;
  /** Where the CTA goes. Null only when `state` is 'none'. */
  href: string | null;
  /** The badge beside Resume Draft: "Never generated" / "Unsaved changes". */
  note: string | null;
  /** The second button ("Edit draft" beside "View lease"). */
  secondaryLabel: string | null;
  secondaryHref: string | null;
}

export interface HouseholdListRow {
  /** `<caseId>`, or `group:<groupId>` for a PRE-CASE applicant group (T15). */
  householdId: string;
  caseId: string | null;
  groupId: string | null;
  name: string;
  propertyLabel: string | null;
  unitLabel: string | null;
  relationship: HouseholdRelationship;
  standing: WhereItStands;
  inMotionCount: number;
  signals: SignalCounts;
  lastActivityAt: string | null;
  state: 'open' | 'closed';
  /** The operational overview the Applications page carried (sol finding 10). */
  intake: HouseholdIntakeSummary;
  verification: HouseholdVerificationSummary;
  lease: HouseholdLeaseSummary;
  /**
   * The AppFolio coordinates of the household's unit, carried so a Send surface
   * can PRESELECT it. ⚠️ Identifiers, never display — the row already carries
   * `propertyLabel` / `unitLabel` for the words.
   */
  propertyId: string | null;
  unitId: string | null;
}

/** GET /api/households. */
export interface HouseholdListResponse {
  items: HouseholdListRow[];
  total: number;
  nextCursor: string | null;
  withheld: DocWithheldNote[];
  generatedAt: string;
}

/** One person on the household header. Alias of the case file's person shape. */
export type HouseholdPerson = CasePerson;

/** One header action ('builder', 'renewal', 'payment-plan'…). */
export type HouseholdAction = CaseAction;

/** One group inside a durable case. */
export type HouseholdDurableCaseMember = DurableCaseMember;

/** The durable-case block, carried through from the case file unchanged. */
export type HouseholdDurableCase = DurableCasePayload;

/**
 * One line of the household-wide timeline. Alias of the shape the case file
 * already renders.
 *
 * ⚠️ Newest FIRST (and capped at 50) — the OPPOSITE of
 * `SentItemDetailResponse.activity`, which is newest LAST (T18). Render this
 * one in the order it arrives.
 */
export type HouseholdActivityEvent = ActivityEvent;

/** The current executed lease's terms — from the executed lease's own summary,
 *  never from AppFolio (a known mirror gap). */
export interface HouseholdLeaseTerm {
  start: string;
  end: string;
  rent: string;
}

/** The household detail header (§7.5). */
export interface HouseholdDetailHeader {
  name: string;
  people: HouseholdPerson[];
  propertyLabel: string | null;
  unitLabel: string | null;
  relationship: HouseholdRelationship;
  afStatusLabel: string | null;
  freshAsOf: string | null;
  leaseTerm?: HouseholdLeaseTerm;
  actions: HouseholdAction[];
  standing: WhereItStands;
}

/** GET /api/households/:householdId. */
export interface HouseholdDetailResponse {
  householdId: string;
  caseId: string | null;
  groupId: string | null;
  header: HouseholdDetailHeader;
  /** In motion in `waiting` order, then finished newest first (§8.3). Empty
   *  but for a lease draft on a pre-case `group:<groupId>` (T15). */
  items: SentItemRow[];
  signals: Signal[];
  /** The household-wide timeline (§8.3's last section). Newest FIRST. */
  activity: HouseholdActivityEvent[];
  /** The standing C9 line, keyed on the VIEWER — on every household file. */
  withheld: DocWithheldNote[];
  durableCase?: HouseholdDurableCase | null;
  generatedAt: string;
}

// ---------------------------------------------------------------------------
// Id mapping (W5) — §7.3 on the client, so an old /threads or /docs URL can be
// redirected before the browser navigates.
//
// The implementations live in `./sentItemIds` rather than here, and are
// re-exported so callers keep one import. The reason is testability: this
// module imports `@/lib/api` for the fetchers below, which reads
// `import.meta.env` at load and therefore only runs under Vite or the
// module-mock flag (T21). The mapping is the part that has to be RIGHT — it
// stays in a module a bare `npx tsx --test` can import.
// ---------------------------------------------------------------------------

export { itemIdForDocParams, itemIdForThreadId } from '@/lib/sentItemIds';

// ---------------------------------------------------------------------------
// Fetchers (W4) — types above are the parity-checked contract; everything
// below is wiring and is NOT parity-checked (see the header note).
// ---------------------------------------------------------------------------

/**
 * The §7.1 query encoding, as one pure function so the page, the tests and the
 * fetcher cannot disagree about what a filter is called on the wire.
 *
 * Every key is omitted when it is unset — a `state=` or `types=` with nothing
 * after it is a filter the server has to decide how to read, and "empty means
 * everything" is a rule better not written twice.
 */
export function sentItemsQuery(params: SentItemsListParams = {}): string {
  const sp = new URLSearchParams();
  if (params.view) sp.set('view', params.view);
  if (params.types && params.types.length > 0) sp.set('types', params.types.join(','));
  // Field name mirrored verbatim from SentItemsListParams so the two sides
  // cannot drift on a name nobody wrote down (§6.2 "Renewal" chip).
  if (params.renewalsOnly) sp.set('renewalsOnly', '1');
  if (params.state) sp.set('state', params.state);
  if (params.step) sp.set('step', params.step);
  if (params.phase) sp.set('phase', params.phase);
  if (params.excludeRenewals) sp.set('excludeRenewals', '1');
  if (params.party) sp.set('party', params.party);
  if (params.blocked) sp.set('blocked', '1');
  if (params.q && params.q.trim()) sp.set('q', params.q.trim());
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.cursor) sp.set('cursor', params.cursor);
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.scope) sp.set('scope', params.scope);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** GET /api/sent-items/lanes — the Overview's workflow lanes, counted by the
 *  server over the same rows the list serves. */
export function fetchSentItemsLanes(): Promise<SentItemsLanesResponse> {
  return api.get<SentItemsLanesResponse>('/api/sent-items/lanes');
}

// ---------------------------------------------------------------------------
// The signers report (sol finding 17)
// ---------------------------------------------------------------------------

/**
 * Whose move a pending signature is.
 *
 * A NARROWER union than `WaitingParty`: this report is about people, and
 * `'system'` is not one. An item waiting on a machine contributes no rows.
 */
export type SentItemSignerParty = 'whp' | 'recipient';

/** One person, as the report names them. Never a role token, never an id. */
export interface SentItemSigner {
  name: string;
  /** "Resident 1", "Broker", "Previous landlord" — humanised server-side. */
  role: string;
  party: SentItemSignerParty;
}

/** Where this signer sits in the document's roster: "2 of 3". */
export interface SentItemSignerPosition {
  index: number;
  total: number;
}

/**
 * One pending signature, across every in-flight item.
 *
 * A SECONDARY report over the Sent Items corpus, not a second canonical list:
 * no state, no signals, no actions — `href` is the item's Full Details, which
 * is where all of that lives.
 */
export interface SentItemSignerRow {
  itemId: string;
  kind: SentItemKind;
  kindLabel: string;
  subject: string;
  propertyLabel: string | null;
  unitLabel: string | null;
  signer: SentItemSigner;
  /** Null when the item has no signing roster (an intake, a verification). */
  position: SentItemSignerPosition | null;
  daysWaiting: number;
  /** ISO instant THIS signer's wait began. Never their last reminder. */
  since: string;
  sentAt: string | null;
  lastReminderAt: string | null;
  lastOpenedAt: string | null;
  href: string;
}

/** GET /api/sent-items/submissions query params. */
export interface SentItemSignersParams {
  q?: string;
  party?: SentItemSignerParty;
}

/** GET /api/sent-items/submissions. */
export interface SentItemSignersResponse {
  /** Longest wait first. The SERVER sorts; the page renders what it is given. */
  rows: SentItemSignerRow[];
  total: number;
  withheld: DocWithheldNote[];
  generatedAt: string;
}

/** GET /api/sent-items (§7.1). The SERVER sorts; a page is rendered as given. */
export function fetchSentItems(
  params: SentItemsListParams = {},
): Promise<SentItemsListResponse> {
  return api.get<SentItemsListResponse>(`/api/sent-items${sentItemsQuery(params)}`);
}

/**
 * GET /api/sent-items/:itemId (§7.2). `version` selects a version exactly as
 * /api/docs does; omit it for the current one.
 *
 * The id is `<kind>:<nativeId>` and can carry a second colon (a rental
 * verification's `#<sk>`), so it is encoded as ONE path segment.
 */
export function fetchSentItem(
  itemId: string,
  version?: string | null,
): Promise<SentItemDetailResponse> {
  const q = version ? `?version=${encodeURIComponent(version)}` : '';
  return api.get<SentItemDetailResponse>(
    `/api/sent-items/${encodeURIComponent(itemId)}${q}`,
  );
}

/**
 * The reason a 2xx response FAILED, or null when it did not.
 *
 * Several staff endpoints answer HTTP 200 for a request they refused:
 * `POST /api/requests/cancel` and `/resend` return `{ failed: n, results: [{ ok:
 * false, reason }] }`, and the dedicated cancels / the owner endpoints return
 * `{ ok: false }` or `{ cancelled: false }` on some paths. The panel used to see
 * "no exception" and toast "done" over an action that did nothing (the move-out
 * funds Cancel, WEL-181/182 audit). Read every shape here, in one place, and
 * surface the SERVER's reason — never a bare "failed".
 */
export function actionFailureReason(res: unknown): string | null {
  if (res === null || typeof res !== 'object') return null;
  const body = res as {
    failed?: unknown;
    sent?: unknown;
    ok?: unknown;
    cancelled?: unknown;
    reason?: unknown;
    message?: unknown;
    error?: unknown;
    results?: unknown;
  };
  // A RESEND that reached somebody is not a failure, even when it could not reach everybody
  // (one signer has no email): `{ ok: sent > 0, sent, failed }`. Reporting it as one invites a
  // retry that would email the people who were already reached. `partialSendNotice` says it.
  // A CANCEL has no `sent` — a cancel that failed for anyone stays strictly a failure.
  if (isPartialSend(body)) return null;
  const text = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() ? v.trim() : null;
  const firstFailedResult = firstFailedOf(body.results);
  const failedCount = typeof body.failed === 'number' ? body.failed : 0;
  if (failedCount > 0 || firstFailedResult || body.ok === false || body.cancelled === false) {
    return (
      text(firstFailedResult?.reason) ??
      text(body.reason) ??
      text(body.message) ??
      text(body.error) ??
      "That didn't go through."
    );
  }
  return null;
}

function firstFailedOf(results: unknown): { ok?: unknown; reason?: unknown } | undefined {
  return Array.isArray(results)
    ? (results as Array<{ ok?: unknown; reason?: unknown }>).find((r) => r?.ok === false)
    : undefined;
}

/** `sent > 0` and `failed > 0`, and the server did not call it a failure (`ok !== false`). */
function isPartialSend(body: { sent?: unknown; failed?: unknown; ok?: unknown }): boolean {
  return (
    typeof body.sent === 'number' &&
    body.sent > 0 &&
    typeof body.failed === 'number' &&
    body.failed > 0 &&
    body.ok !== false
  );
}

/**
 * The sentence for a Resend that reached some people and not others — "Sent to 2; 1 couldn't
 * be sent: no email on file." — or null when it was not partial. The caller shows it as a
 * WARNING, not a success and not an error: the reminder went out, and the staffer needs to
 * know who did not get it.
 */
export function partialSendNotice(res: unknown): string | null {
  if (res === null || typeof res !== 'object') return null;
  const body = res as { sent?: unknown; failed?: unknown; ok?: unknown; results?: unknown };
  if (!isPartialSend(body)) return null;
  const reason = firstFailedOf(body.results)?.reason;
  const why = typeof reason === 'string' && reason.trim() ? reason.trim().replace(/[.\s]+$/, '') : '';
  return `Sent to ${body.sent}; ${body.failed} couldn't be sent${why ? `: ${why}` : ''}.`;
}

/**
 * Run one action from `SentItemDetailResponse.actions`.
 *
 * The action carries its OWN endpoint, method and body (§6.6) — this function
 * deliberately knows nothing about what any of them do, because every guard
 * that matters (RRA access, the W-9 refusals, the revision-CAS on every
 * writer) lives server-side where it already is. Re-deriving a payload here
 * would be a second copy of a rule the server owns.
 *
 * It does know one thing about the RESPONSE: a 200 that says it failed is a
 * failure (`actionFailureReason`), so it throws with the server's reason and no
 * caller can report "done" over a refusal.
 *
 * `'navigate'` is not a request: it is an href for the router, and calling
 * this with one is a programming error rather than a runtime failure to
 * surface to an operator.
 */
export async function runItemAction(action: ItemAction): Promise<unknown> {
  let res: unknown;
  switch (action.method) {
    case 'GET':
      // Data, not a mutation — nothing to refuse.
      return api.get(action.endpoint);
    case 'POST':
      res = await api.post(action.endpoint, action.body);
      break;
    case 'PATCH':
      res = await api.patch(action.endpoint, action.body);
      break;
    case 'DELETE':
      res = await api.del(action.endpoint);
      break;
    case 'navigate':
      throw new Error(`"${action.id}" is a navigation action — route to it, don't post it.`);
  }
  const failure = actionFailureReason(res);
  if (failure) throw new Error(failure);
  return res;
}

/**
 * The signers-report query, as one pure function so the page, its tests and the
 * fetcher cannot disagree about what a filter is called on the wire.
 */
export function sentItemSignersQuery(params: SentItemSignersParams = {}): string {
  const sp = new URLSearchParams();
  if (params.q && params.q.trim()) sp.set('q', params.q.trim());
  if (params.party) sp.set('party', params.party);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * GET /api/sent-items/submissions — every pending signature, longest wait first.
 *
 * The server sorts. The page renders the rows in the order it is given, for the
 * same reason `SentItemList` does: a client-side re-sort is a second opinion
 * about a question the server already answered.
 */
export function fetchSentItemSigners(
  params: SentItemSignersParams = {},
): Promise<SentItemSignersResponse> {
  return api.get<SentItemSignersResponse>(
    `/api/sent-items/submissions${sentItemSignersQuery(params)}`,
  );
}
